import axios from 'axios'
import moment from 'moment'
import { API_TIMEOUT } from '@/main'
import store from '../vuex/store'

const COMPETITIONS_PER_REQUEST = 25
const TEAMS_PER_REQUEST = 6

const apiClient = axios.create({
  timeout: API_TIMEOUT,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
})

function getAPIURL () {
  return store.state.apiUrl
}

function getToken () {
  return store.state.token
}

function authConfig () {
  return {
    headers: { Authorization: 'Bearer ' + getToken(), 'Content-Type': 'application/json' }
  }
}

const BETGAME_SELECT = 'id,icon,name,bg_betgame_competition(bg_competition(type))'
const SCHEDULE_SELECT = 'id,goalshome,goalsguest,livegoalshome,livegoalsguest,status,matchdate,fk_competition,fk_season,fk_team_home,fk_team_guest'

export function competitionTypeFromBetgame (betgame) {
  const rows = betgame && betgame.bg_betgame_competition
  const comp = rows && rows.length > 0 && rows[0].bg_competition
  return comp ? comp.type : undefined
}

export function mapOwnedBetgame (row) {
  return {
    id: row.id,
    icon: row.icon,
    name: row.name,
    type: competitionTypeFromBetgame(row)
  }
}

export function mapLinkedBetgame (row) {
  const bg = row.bg_betgame || {}
  return {
    id: row.fk_betgame,
    icon: bg.icon,
    name: bg.name,
    type: competitionTypeFromBetgame(bg)
  }
}

export function mapParticipantBetgame (row) {
  const bg = row.bg_betgame || {}
  return {
    id: row.fk_betgame,
    icon: bg.icon,
    name: bg.name,
    type: competitionTypeFromBetgame(bg)
  }
}

export function isTodayFutureMatch (match, now = new Date()) {
  if (!match || !match.matchdate || match.status === 'verlegt') {
    return false
  }
  const matchDate = new Date(match.matchdate)
  return matchDate.toDateString() === now.toDateString() && matchDate > now
}

export function countMatchStates (matches) {
  let unfinished = 0
  let running = 0
  let finished = 0
  for (let i = 0; i < matches.length; i++) {
    const m = matches[i]
    const isUnfinished = m.goalshome == null && m.goalsguest == null
    if (isUnfinished) {
      if (m.livegoalshome != null && m.livegoalsguest != null) {
        running++
      } else {
        unfinished++
      }
    } else if (
      (m.goalshome != null && m.goalsguest != null) ||
      m.status === 'annulliert' ||
      m.status === 'nicht ausgetragen'
    ) {
      finished++
    }
  }
  return {
    count: matches.length,
    unfinished,
    running,
    finished
  }
}

function groupMatchesByBetgame (rows, betgameKey) {
  const grouped = {}
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const betgameId = row[betgameKey]
    const match = row.bg_v_schedule
    if (!betgameId || !match) {
      continue
    }
    if (!grouped[betgameId]) {
      grouped[betgameId] = []
    }
    grouped[betgameId].push(match)
  }
  return grouped
}

export function buildBattleStats (battleRows, betgameIds) {
  const grouped = groupMatchesByBetgame(battleRows, 'fk_betgame')
  return betgameIds
    .filter(function (id) { return grouped[id] && grouped[id].length > 0 })
    .map(function (id) {
      const stats = countMatchStates(grouped[id])
      return { id: id, count: stats.count, unfinished: stats.unfinished, running: stats.running, finished: stats.finished }
    })
}

function matchInvolvesTeam (match, teamId) {
  return match.fk_team_home === teamId || match.fk_team_guest === teamId
}

export function buildTeamStats (teamRows, scheduleMatches, betgameIds) {
  const teamsByBetgame = {}
  for (let i = 0; i < teamRows.length; i++) {
    const row = teamRows[i]
    if (!teamsByBetgame[row.fk_betgame]) {
      teamsByBetgame[row.fk_betgame] = []
    }
    teamsByBetgame[row.fk_betgame].push(row)
  }
  return betgameIds
    .filter(function (id) { return teamsByBetgame[id] && teamsByBetgame[id].length > 0 })
    .map(function (id) {
      const teams = teamsByBetgame[id]
      const matches = []
      for (let m = 0; m < scheduleMatches.length; m++) {
        const match = scheduleMatches[m]
        for (let t = 0; t < teams.length; t++) {
          const team = teams[t]
          if (match.fk_season === team.fk_season && matchInvolvesTeam(match, team.fk_team)) {
            matches.push(match)
            break
          }
        }
      }
      const stats = countMatchStates(matches)
      return { id: id, count: stats.count, unfinished: stats.unfinished, running: stats.running, finished: stats.finished }
    })
}

export function buildTeamCompetitionStats (teamCompRows, scheduleMatches, betgameIds) {
  const teamsByBetgame = {}
  for (let i = 0; i < teamCompRows.length; i++) {
    const row = teamCompRows[i]
    if (!teamsByBetgame[row.fk_betgame]) {
      teamsByBetgame[row.fk_betgame] = []
    }
    teamsByBetgame[row.fk_betgame].push(row)
  }
  return betgameIds
    .filter(function (id) { return teamsByBetgame[id] && teamsByBetgame[id].length > 0 })
    .map(function (id) {
      const teams = teamsByBetgame[id]
      const matches = []
      for (let m = 0; m < scheduleMatches.length; m++) {
        const match = scheduleMatches[m]
        for (let t = 0; t < teams.length; t++) {
          const team = teams[t]
          if (
            match.fk_season === team.fk_season &&
            match.fk_competition === team.fk_competition &&
            matchInvolvesTeam(match, team.fk_team)
          ) {
            matches.push(match)
            break
          }
        }
      }
      const stats = countMatchStates(matches)
      return { id: id, count: stats.count, unfinished: stats.unfinished, running: stats.running, finished: stats.finished }
    })
}

function uniqueMatchIds (ids) {
  const seen = {}
  const result = []
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i]
    if (id != null && !seen[id]) {
      seen[id] = true
      result.push(id)
    }
  }
  return result
}

export function buildMissingBets ({
  participantBetgameIds,
  competitions,
  battleRows,
  teamRows,
  teamCompRows,
  leagueMatches,
  teamScheduleMatches,
  userBets,
  now = new Date()
}) {
  const compByBetgame = {}
  for (let i = 0; i < competitions.length; i++) {
    const row = competitions[i]
    if (!compByBetgame[row.fk_betgame]) {
      compByBetgame[row.fk_betgame] = []
    }
    compByBetgame[row.fk_betgame].push(row)
  }

  const battleMatchesByBetgame = groupMatchesByBetgame(battleRows, 'fk_betgame')

  const betsByBetgame = {}
  for (let i = 0; i < userBets.length; i++) {
    const bet = userBets[i]
    if (!betsByBetgame[bet.fk_betgame]) {
      betsByBetgame[bet.fk_betgame] = []
    }
    betsByBetgame[bet.fk_betgame].push(bet.fk_match)
  }

  const result = []
  for (let i = 0; i < participantBetgameIds.length; i++) {
    const betgameId = participantBetgameIds[i]
    const matchIds = []

    const comps = compByBetgame[betgameId] || []
    for (let c = 0; c < comps.length; c++) {
      const comp = comps[c]
      for (let m = 0; m < leagueMatches.length; m++) {
        const match = leagueMatches[m]
        if (match.fk_competition === comp.fk_competition && match.fk_season === comp.fk_season && isTodayFutureMatch(match, now)) {
          matchIds.push(match.id)
        }
      }
    }

    const battleMatches = battleMatchesByBetgame[betgameId] || []
    for (let b = 0; b < battleMatches.length; b++) {
      if (isTodayFutureMatch(battleMatches[b], now)) {
        matchIds.push(battleMatches[b].id)
      }
    }

    const teams = teamRows.filter(function (row) { return row.fk_betgame === betgameId })
    for (let t = 0; t < teams.length; t++) {
      const team = teams[t]
      for (let m = 0; m < teamScheduleMatches.length; m++) {
        const match = teamScheduleMatches[m]
        if (match.fk_season === team.fk_season && matchInvolvesTeam(match, team.fk_team) && isTodayFutureMatch(match, now)) {
          matchIds.push(match.id)
        }
      }
    }

    const teamComps = teamCompRows.filter(function (row) { return row.fk_betgame === betgameId })
    for (let tc = 0; tc < teamComps.length; tc++) {
      const team = teamComps[tc]
      for (let m = 0; m < teamScheduleMatches.length; m++) {
        const match = teamScheduleMatches[m]
        if (
          match.fk_season === team.fk_season &&
          match.fk_competition === team.fk_competition &&
          matchInvolvesTeam(match, team.fk_team) &&
          isTodayFutureMatch(match, now)
        ) {
          matchIds.push(match.id)
        }
      }
    }

    const todayMatchIds = uniqueMatchIds(matchIds)
    const betMatches = betsByBetgame[betgameId] || []
    let betcount = 0
    for (let b = 0; b < betMatches.length; b++) {
      if (todayMatchIds.indexOf(betMatches[b]) !== -1) {
        betcount++
      }
    }

    result.push({
      id: betgameId,
      matchcount: todayMatchIds.length,
      betcount: betcount
    })
  }
  return result
}

function chunkArray (items, chunkSize) {
  const chunks = []
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize))
  }
  return chunks
}

export function uniqueCompetitionPairs (competitions) {
  const seen = {}
  const unique = []
  for (let i = 0; i < competitions.length; i++) {
    const row = competitions[i]
    const key = row.fk_competition + ':' + row.fk_season
    if (!seen[key]) {
      seen[key] = true
      unique.push({
        fk_competition: row.fk_competition,
        fk_season: row.fk_season
      })
    }
  }
  return unique
}

export function uniqueTeamQueries (teams) {
  const seen = {}
  const unique = []
  for (let i = 0; i < teams.length; i++) {
    const team = teams[i]
    const key = team.fk_team + ':' + team.fk_season + ':' + (team.fk_competition || '')
    if (!seen[key]) {
      seen[key] = true
      unique.push({
        fk_team: team.fk_team,
        fk_season: team.fk_season,
        fk_competition: team.fk_competition
      })
    }
  }
  return unique
}

function groupBySeason (items) {
  const grouped = {}
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    const seasonId = item.fk_season
    if (!grouped[seasonId]) {
      grouped[seasonId] = []
    }
    grouped[seasonId].push(item)
  }
  return grouped
}

function buildSeasonCompetitionFilter (seasonId, competitionIds, extraFilters) {
  let filter = 'fk_season=eq.' + seasonId
  if (competitionIds.length === 1) {
    filter += '&fk_competition=eq.' + competitionIds[0]
  } else {
    filter += '&fk_competition=in.(' + competitionIds.join(',') + ')'
  }
  return filter + (extraFilters || '')
}

function buildTeamOrClause (team) {
  const teamFilter = 'or(fk_team_home.eq.' + team.fk_team + ',fk_team_guest.eq.' + team.fk_team + ')'
  if (team.fk_competition) {
    return 'and(fk_competition.eq.' + team.fk_competition + ',' + teamFilter + ')'
  }
  return teamFilter
}

function buildSeasonTeamFilter (seasonId, teams) {
  if (teams.length === 1) {
    const team = teams[0]
    let filter = 'fk_season=eq.' + seasonId
    if (team.fk_competition) {
      filter += '&fk_competition=eq.' + team.fk_competition
    }
    filter += '&or=(fk_team_home.eq.' + team.fk_team + ',fk_team_guest.eq.' + team.fk_team + ')'
    return filter
  }
  let filter = 'fk_season=eq.' + seasonId + '&or=('
  for (let i = 0; i < teams.length; i++) {
    filter += buildTeamOrClause(teams[i])
    if (i < teams.length - 1) {
      filter += ','
    }
  }
  filter += ')'
  return filter
}

export function buildTodayScheduleFilter (now = new Date()) {
  const dayStart = moment(now).startOf('day').format('YYYY-MM-DDTHH:mm:ss')
  const dayEnd = moment(now).endOf('day').format('YYYY-MM-DDTHH:mm:ss')
  return '&matchdate=gte.' + dayStart + '&matchdate=lte.' + dayEnd + '&status=neq.verlegt'
}

export function buildCompetitionScheduleFilters (competitions, options) {
  const opts = options || {}
  const todayOnly = opts.todayOnly === true
  const now = opts.now || new Date()
  const extraFilters = todayOnly ? buildTodayScheduleFilter(now) : ''
  const bySeason = groupBySeason(uniqueCompetitionPairs(competitions))
  const filters = []

  Object.keys(bySeason).forEach(function (seasonId) {
    const competitionIds = bySeason[seasonId].map(function (row) { return row.fk_competition })
    chunkArray(competitionIds, COMPETITIONS_PER_REQUEST).forEach(function (chunk) {
      filters.push(buildSeasonCompetitionFilter(seasonId, chunk, extraFilters))
    })
  })

  return filters
}

export function buildTeamScheduleFilters (teams) {
  const bySeason = groupBySeason(uniqueTeamQueries(teams))
  const filters = []

  Object.keys(bySeason).forEach(function (seasonId) {
    chunkArray(bySeason[seasonId], TEAMS_PER_REQUEST).forEach(function (chunk) {
      filters.push(buildSeasonTeamFilter(seasonId, chunk))
    })
  })

  return filters
}

export function mergeUniqueScheduleMatches (matchLists) {
  const seen = {}
  const result = []
  for (let i = 0; i < matchLists.length; i++) {
    const matches = matchLists[i]
    for (let j = 0; j < matches.length; j++) {
      const match = matches[j]
      if (match && match.id != null && !seen[match.id]) {
        seen[match.id] = true
        result.push(match)
      }
    }
  }
  return result
}

async function fetchScheduleMatchesBatched (filters) {
  if (!filters || filters.length === 0) {
    return []
  }
  const responses = await Promise.all(filters.map(function (filter) {
    return getScheduleMatches(filter)
  }))
  return mergeUniqueScheduleMatches(responses.map(function (response) {
    return response.data || []
  }))
}

function getBetgameIdsCsv (betgameIds) {
  return betgameIds.join(',')
}

export function getBetgamesOwned (userId) {
  return apiClient.get(
    getAPIURL() + '/bg_betgame?fk_user=eq.' + userId + '&select=' + BETGAME_SELECT,
    authConfig()
  )
}

export function getBetgamesAuthor (userId) {
  return apiClient.get(
    getAPIURL() + '/bg_author?fk_user=eq.' + userId + '&select=fk_betgame,bg_betgame(' + BETGAME_SELECT + ')',
    authConfig()
  )
}

export function getBetgamesParticipant (userId) {
  return apiClient.get(
    getAPIURL() + '/bg_user_betgame?fk_user=eq.' + userId + '&select=fk_betgame,bg_betgame(' + BETGAME_SELECT + ')',
    authConfig()
  )
}

export function getUserBetgameCompetitions (betgameIds) {
  if (!betgameIds || betgameIds.length === 0) {
    return Promise.resolve({ data: [] })
  }
  return apiClient.get(
    getAPIURL() + '/bg_betgame_competition?fk_betgame=in.(' + getBetgameIdsCsv(betgameIds) + ')&select=fk_betgame,fk_competition,fk_season',
    authConfig()
  )
}

export function getUserBattleMatches (betgameIds) {
  if (!betgameIds || betgameIds.length === 0) {
    return Promise.resolve({ data: [] })
  }
  return apiClient.get(
    getAPIURL() + '/bg_betgame_match?fk_betgame=in.(' + getBetgameIdsCsv(betgameIds) + ')&select=fk_betgame,bg_v_schedule(' + SCHEDULE_SELECT + ')',
    authConfig()
  )
}

export function getUserBetgameTeams (betgameIds) {
  if (!betgameIds || betgameIds.length === 0) {
    return Promise.resolve({ data: [] })
  }
  return apiClient.get(
    getAPIURL() + '/bg_betgame_team?fk_betgame=in.(' + getBetgameIdsCsv(betgameIds) + ')&select=fk_betgame,fk_team,fk_season',
    authConfig()
  )
}

export function getUserBetgameTeamCompetitions (betgameIds) {
  if (!betgameIds || betgameIds.length === 0) {
    return Promise.resolve({ data: [] })
  }
  return apiClient.get(
    getAPIURL() + '/bg_betgame_team_competition?fk_betgame=in.(' + getBetgameIdsCsv(betgameIds) + ')&select=fk_betgame,fk_team,fk_season,fk_competition',
    authConfig()
  )
}

export function getUserBetsForBetgames (userId, betgameIds) {
  if (!betgameIds || betgameIds.length === 0) {
    return Promise.resolve({ data: [] })
  }
  return apiClient.get(
    getAPIURL() + '/bg_bet?fk_user=eq.' + userId + '&fk_betgame=in.(' + getBetgameIdsCsv(betgameIds) + ')&select=fk_betgame,fk_match',
    authConfig()
  )
}

export function getScheduleMatches (whereClause) {
  if (!whereClause) {
    return Promise.resolve({ data: [] })
  }
  return apiClient.get(
    getAPIURL() + '/bg_v_schedule?select=' + SCHEDULE_SELECT + '&' + whereClause,
    authConfig()
  )
}

export async function fetchUserBetgamesBattlesTeams (userId) {
  const [ownerRes, authorRes, participantRes] = await Promise.all([
    getBetgamesOwned(userId),
    getBetgamesAuthor(userId),
    getBetgamesParticipant(userId)
  ])

  const betgames_owner = (ownerRes.data || []).map(mapOwnedBetgame)
  const betgames_author = (authorRes.data || []).map(mapLinkedBetgame)
  const betgames_participant = (participantRes.data || []).map(mapParticipantBetgame)

  const participantBetgameIds = betgames_participant.map(function (row) { return row.id })
  if (participantBetgameIds.length === 0) {
    return {
      betgames_owner,
      betgames_author,
      betgames_participant,
      missing_bets: [],
      battles: [],
      teams: [],
      teams_competitions: []
    }
  }

  const [
    competitionsRes,
    battleRes,
    teamRes,
    teamCompRes,
    userBetsRes
  ] = await Promise.all([
    getUserBetgameCompetitions(participantBetgameIds),
    getUserBattleMatches(participantBetgameIds),
    getUserBetgameTeams(participantBetgameIds),
    getUserBetgameTeamCompetitions(participantBetgameIds),
    getUserBetsForBetgames(userId, participantBetgameIds)
  ])

  const competitions = competitionsRes.data || []
  const battleRows = battleRes.data || []
  const teamRows = teamRes.data || []
  const teamCompRows = teamCompRes.data || []
  const userBets = userBetsRes.data || []

  const teamQueryRows = teamRows.concat(teamCompRows)
  const leagueScheduleFilters = buildCompetitionScheduleFilters(competitions, { todayOnly: true })
  const teamScheduleFilters = buildTeamScheduleFilters(teamQueryRows)

  const [leagueMatches, teamScheduleMatches] = await Promise.all([
    fetchScheduleMatchesBatched(leagueScheduleFilters),
    fetchScheduleMatchesBatched(teamScheduleFilters)
  ])

  const allScheduleMatches = mergeUniqueScheduleMatches([leagueMatches, teamScheduleMatches])
  const missingBetgameIds = competitions.map(function (row) { return row.fk_betgame })
    .filter(function (id, index, arr) { return arr.indexOf(id) === index })

  return {
    betgames_owner,
    betgames_author,
    betgames_participant,
    missing_bets: buildMissingBets({
      participantBetgameIds: missingBetgameIds,
      competitions,
      battleRows,
      teamRows,
      teamCompRows,
      leagueMatches,
      teamScheduleMatches,
      userBets
    }),
    battles: buildBattleStats(battleRows, participantBetgameIds),
    teams: buildTeamStats(teamRows, allScheduleMatches, participantBetgameIds),
    teams_competitions: buildTeamCompetitionStats(teamCompRows, allScheduleMatches, participantBetgameIds)
  }
}
